import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Form } from "@av/components/atoms/form/Form";
import { bffAxios } from "@av/config/api-config/axios-config";
import { AllProvinces } from "@av/types/province";

import type {
  Candidate,
  CandidateDataUI,
  UploadFileExtended,
} from "../../../../services/apply/types";
import type { FormInstance } from "antd";
import type { AxiosError } from "axios";

export type AvaResumeAutofillStatus =
  | "noResume"
  | "ready"
  | "parsing"
  | "success"
  | "partial"
  | "error"
  | "reupload"
  | "undo";

// Ava does not parse a middle name from resumes, so it is excluded from autofill/marker handling.
export type AvaResumeAutofillField = Exclude<keyof Candidate, "middleName">;

const AUTOFILL_FIELDS: AvaResumeAutofillField[] = [
  "firstName",
  "lastName",
  "email",
  "phoneNumber",
  "linkedIn",
  "subdivision",
  "city",
];

type CandidateSnapshot = Partial<Record<AvaResumeAutofillField, string | undefined>>;

function emptyCandidateField(field: AvaResumeAutofillField) {
  return field === "subdivision" ? undefined : "";
}

type ResumeExtraction = {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  linkedIn: string | null;
  country: string | null;
  subdivision: string | null;
  city: string | null;
};

type ResumeExtractionResponse = {
  data: ResumeExtraction;
};

type ResumeExtractionErrorResponse = {
  error?: { message?: string };
};

function resumeIdentity(resume?: UploadFileExtended) {
  if (!resume) return undefined;

  return [resume.uid, resume.name, resume.size ?? ""].join(":");
}

function mapExtraction(extraction: ResumeExtraction) {
  const subdivisionCode =
    extraction.country === "CA" && extraction.subdivision
      ? `${extraction.country}-${extraction.subdivision}`
      : null;
  const subdivision = AllProvinces.some((province) => province === subdivisionCode)
    ? subdivisionCode
    : null;
  const extractedFields: Record<AvaResumeAutofillField, string | null> = {
    firstName: extraction.firstName,
    lastName: extraction.lastName,
    email: extraction.email,
    phoneNumber: extraction.phoneNumber,
    linkedIn: extraction.linkedIn,
    subdivision,
    city: extraction.city,
  };

  return AUTOFILL_FIELDS.reduce<{
    values: CandidateSnapshot;
    populatedFields: AvaResumeAutofillField[];
    unfilledFields: AvaResumeAutofillField[];
  }>(
    (result, field) => {
      const value = extractedFields[field];

      if (value === null) {
        result.unfilledFields.push(field);
      } else {
        result.values[field] = value;
        result.populatedFields.push(field);
      }

      return result;
    },
    { values: {}, populatedFields: [], unfilledFields: [] }
  );
}

function setCandidateFields(form: FormInstance<CandidateDataUI>, values: CandidateSnapshot) {
  const candidate = (form.getFieldValue("candidate") ?? {}) as Partial<Candidate>;

  form.setFieldsValue({
    candidate: {
      ...candidate,
      ...values,
    },
  } as Partial<CandidateDataUI>);
}

function clearMatchingCandidateFields(
  form: FormInstance<CandidateDataUI>,
  values: CandidateSnapshot,
  fields: AvaResumeAutofillField[]
) {
  const candidate = (form.getFieldValue("candidate") ?? {}) as Partial<Candidate>;
  const fieldsToClear = fields.reduce<CandidateSnapshot>((valuesToClear, field) => {
    const autofilledValue = values[field];

    if (autofilledValue !== undefined && candidate[field] === autofilledValue) {
      valuesToClear[field] = emptyCandidateField(field);
    }

    return valuesToClear;
  }, {});

  if (Object.keys(fieldsToClear).length > 0) {
    setCandidateFields(form, fieldsToClear);
  }
}

type UseAvaResumeAutofillParams = {
  form: FormInstance<CandidateDataUI>;
  resume?: UploadFileExtended;
};

export function useAvaResumeAutofill({ form, resume }: UseAvaResumeAutofillParams) {
  const [status, setStatus] = useState<AvaResumeAutofillStatus>(resume ? "ready" : "noResume");
  const [autofilledFields, setAutofilledFields] = useState<AvaResumeAutofillField[]>([]);
  const [unfilledFields, setUnfilledFields] = useState<AvaResumeAutofillField[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const candidate = Form.useWatch("candidate", { form, preserve: true }) as
    | Partial<Candidate>
    | undefined;

  const latestResumeRef = useRef(resume);
  const requestSequenceRef = useRef(0);
  const activeResumeIdentityRef = useRef<string | undefined>(undefined);
  const processedResumeIdentityRef = useRef<string | undefined>(undefined);
  const lastAutofilledValuesRef = useRef<CandidateSnapshot>({});
  const ownedAutofilledFieldsRef = useRef<AvaResumeAutofillField[]>([]);
  const resumeId = resumeIdentity(resume);

  useEffect(() => {
    latestResumeRef.current = resume;
  }, [resume]);

  useEffect(() => {
    if (!candidate) return;

    ownedAutofilledFieldsRef.current = ownedAutofilledFieldsRef.current.filter(
      (field) => candidate[field] === lastAutofilledValuesRef.current[field]
    );
    setAutofilledFields((fields) =>
      fields.filter((field) => ownedAutofilledFieldsRef.current.includes(field))
    );
  }, [candidate]);

  useEffect(
    () => () => {
      requestSequenceRef.current += 1;
    },
    []
  );

  useEffect(() => {
    if (!resume) {
      requestSequenceRef.current += 1;
      activeResumeIdentityRef.current = undefined;
      processedResumeIdentityRef.current = undefined;
      clearMatchingCandidateFields(
        form,
        lastAutofilledValuesRef.current,
        ownedAutofilledFieldsRef.current
      );
      setStatus("noResume");
      setAutofilledFields([]);
      setUnfilledFields([]);
      setErrorMessage(undefined);
      lastAutofilledValuesRef.current = {};
      ownedAutofilledFieldsRef.current = [];

      return;
    }

    if (activeResumeIdentityRef.current && activeResumeIdentityRef.current !== resumeId) {
      requestSequenceRef.current += 1;
      activeResumeIdentityRef.current = undefined;
      setAutofilledFields([]);
      setUnfilledFields([]);
      setStatus("ready");

      return;
    }

    if (!processedResumeIdentityRef.current) {
      setStatus((currentStatus) => (currentStatus === "noResume" ? "ready" : currentStatus));

      return;
    }

    if (
      resumeId &&
      processedResumeIdentityRef.current !== resumeId &&
      ["success", "partial", "error", "undo"].includes(status)
    ) {
      setUnfilledFields([]);
      setStatus("reupload");
    }
  }, [form, resume, resumeId, status]);

  const runExtraction = useCallback(async () => {
    const currentResume = latestResumeRef.current;
    const currentResumeId = resumeIdentity(currentResume);
    const file = currentResume?.originFileObj;
    const previousAutofilledFields = autofilledFields;
    const previousUnfilledFields = unfilledFields;

    if (!currentResume || !currentResumeId || !file) {
      setErrorMessage(undefined);
      setStatus(currentResume ? "error" : "noResume");

      return;
    }

    const requestSequence = requestSequenceRef.current + 1;

    requestSequenceRef.current = requestSequence;
    activeResumeIdentityRef.current = currentResumeId;
    setStatus("parsing");
    setAutofilledFields([]);
    setUnfilledFields([]);
    setErrorMessage(undefined);

    try {
      const response = await bffAxios.post<ResumeExtractionResponse>(
        "/public/resumes/extract",
        file,
        { headers: { "Content-Type": "application/octet-stream" } }
      );

      if (
        requestSequenceRef.current !== requestSequence ||
        resumeIdentity(latestResumeRef.current) !== currentResumeId
      ) {
        return;
      }

      const result = mapExtraction(response.data.data);

      activeResumeIdentityRef.current = undefined;
      processedResumeIdentityRef.current = currentResumeId;
      clearMatchingCandidateFields(
        form,
        lastAutofilledValuesRef.current,
        ownedAutofilledFieldsRef.current
      );
      lastAutofilledValuesRef.current = result.values;
      ownedAutofilledFieldsRef.current = result.populatedFields;
      setCandidateFields(form, result.values);
      setAutofilledFields(result.populatedFields);
      setUnfilledFields(result.unfilledFields);
      setStatus(result.unfilledFields.length === 0 ? "success" : "partial");
    } catch (error) {
      if (
        requestSequenceRef.current !== requestSequence ||
        resumeIdentity(latestResumeRef.current) !== currentResumeId
      ) {
        return;
      }

      activeResumeIdentityRef.current = undefined;
      processedResumeIdentityRef.current = currentResumeId;
      const responseMessage = (error as AxiosError<ResumeExtractionErrorResponse>).response?.data
        ?.error?.message;

      setErrorMessage(responseMessage?.trim() || undefined);
      setAutofilledFields(previousAutofilledFields);
      setUnfilledFields(previousUnfilledFields);
      setStatus("error");
    }
  }, [autofilledFields, form, unfilledFields]);

  const requestAutofill = useCallback(() => {
    void runExtraction();
  }, [runExtraction]);

  const undoAutofill = useCallback(() => {
    requestSequenceRef.current += 1;
    activeResumeIdentityRef.current = undefined;

    clearMatchingCandidateFields(
      form,
      lastAutofilledValuesRef.current,
      ownedAutofilledFieldsRef.current
    );

    lastAutofilledValuesRef.current = {};
    ownedAutofilledFieldsRef.current = [];
    setErrorMessage(undefined);
    setAutofilledFields([]);
    setUnfilledFields([]);
    setStatus("undo");
  }, [form]);

  const clearAutofilledFields = useCallback(() => {
    requestSequenceRef.current += 1;
    activeResumeIdentityRef.current = undefined;

    const emptyValues = autofilledFields.reduce<CandidateSnapshot>((fieldsToClear, field) => {
      fieldsToClear[field] = emptyCandidateField(field);

      return fieldsToClear;
    }, {});

    setCandidateFields(form, emptyValues);
    lastAutofilledValuesRef.current = {};
    ownedAutofilledFieldsRef.current = [];
    setErrorMessage(undefined);
    setAutofilledFields([]);
    setUnfilledFields([]);
    setStatus(resume ? "ready" : "noResume");
  }, [autofilledFields, form, resume]);

  const highlightedFields = useMemo(
    () => new Set<AvaResumeAutofillField>(autofilledFields),
    [autofilledFields]
  );
  const unfilledFieldSet = useMemo(
    () => new Set<AvaResumeAutofillField>(unfilledFields),
    [unfilledFields]
  );

  return {
    status,
    errorMessage,
    autofilledFields,
    highlightedFields,
    unfilledFields: unfilledFieldSet,
    requestAutofill,
    undoAutofill,
    clearAutofilledFields,
  };
}
